import React, { useState, useRef } from "react";
import { Box, Typography, Paper, Button, IconButton, alpha, CircularProgress, TextField } from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Language as DomainIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
  Image as ImageIcon,
  CloudUpload as CloudUploadIcon,
  BarChart as AnalyticsIcon,
} from "@mui/icons-material";
import { Site, CMSDomainInfo } from "../../types";
import { DomainSetupModal } from "./DomainSetupModal";

interface SiteSettingsPanelProps {
  site: Site;
  onBack: () => void;
  apiDomain: CMSDomainInfo | null;
  onSetDomain: (domain: string) => Promise<CMSDomainInfo>;
  onVerifyDomain: () => Promise<boolean>;
  onRemoveDomain: () => Promise<void>;
  // Subdomain props
  subdomain: string | null;
  subdomainSuffix: string;
  onSetSubdomain: (subdomain: string) => Promise<void>;
  // Site URL (full_domain from API)
  siteUrl: string | null;
  // Auto re-publish props
  isSitePublished?: boolean;
  isCurrentSitePublished?: boolean; // Is THIS site the one that's published?
  publishedSiteName?: string | null; // Name of the site that's currently published (if different)
  onPublish?: () => Promise<void>;
  onPublishReplace?: () => Promise<void>; // Unpublish other site and publish this one
  // Branding props
  onUploadFavicon: (file: File) => Promise<string>;
  onUploadLogo: (file: File) => Promise<string>;
  onUpdateSite: (updates: Partial<Site>) => Promise<void>;
}

export const SiteSettingsPanel: React.FC<SiteSettingsPanelProps> = ({
  site,
  onBack,
  apiDomain,
  onSetDomain,
  onVerifyDomain,
  onRemoveDomain,
  subdomain,
  subdomainSuffix,
  onSetSubdomain,
  siteUrl,
  isSitePublished,
  isCurrentSitePublished,
  publishedSiteName,
  onPublish,
  onPublishReplace,
  onUploadFavicon,
  onUploadLogo,
  onUpdateSite,
}) => {
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Google Analytics (GA4) Measurement ID — saved to the site on blur.
  const [gaId, setGaId] = useState(site.gaMeasurementId || "");
  const [savingGa, setSavingGa] = useState(false);
  const gaTrimmed = gaId.trim();
  const gaValid = gaTrimmed === "" || /^G-[A-Z0-9]+$/i.test(gaTrimmed);

  const handleSaveGa = async () => {
    if (!gaValid) return;
    const next = gaTrimmed || undefined;
    if ((site.gaMeasurementId || "") === (next || "")) return; // unchanged
    setSavingGa(true);
    try {
      await onUpdateSite({ gaMeasurementId: next });
    } catch (err) {
      console.error("Failed to save GA Measurement ID:", err);
    } finally {
      setSavingGa(false);
    }
  };

  const hasCustomDomain = !!(apiDomain?.domain && apiDomain.domain.trim());
  const isCustomDomainVerified = apiDomain?.is_domain_verified || false;
  const defaultSubdomain = site.slug || site.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  // Handle favicon upload
  const handleFaviconChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFavicon(true);
    try {
      const url = await onUploadFavicon(file);
      await onUpdateSite({ favicon: url });
    } catch (err) {
      console.error("Failed to upload favicon:", err);
    } finally {
      setUploadingFavicon(false);
      if (faviconInputRef.current) faviconInputRef.current.value = "";
    }
  };

  // Handle logo upload
  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    try {
      const url = await onUploadLogo(file);
      await onUpdateSite({ logo: url });
    } catch (err) {
      console.error("Failed to upload logo:", err);
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  // Handle remove favicon
  const handleRemoveFavicon = async () => {
    await onUpdateSite({ favicon: undefined });
  };

  // Handle remove logo
  const handleRemoveLogo = async () => {
    await onUpdateSite({ logo: undefined });
  };

  return (
    <Box sx={{ height: "100%", bgcolor: "grey.50", overflow: "auto" }}>
      {/* Header */}
      <Paper
        square
        elevation={0}
        sx={{
          height: 56,
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 3,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "white",
        }}
      >
        <IconButton onClick={onBack} size="small" sx={{ color: "text.secondary" }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={600}>
          Site Settings
        </Typography>
      </Paper>

      {/* Content */}
      <Box sx={{ maxWidth: 800, mx: "auto", p: 4 }}>
        {/* Site Info */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Site Information
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Site Name
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {site.name}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Slug
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {site.slug}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Branding */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <ImageIcon sx={{ color: "primary.main" }} />
            <Typography variant="h6" fontWeight={600}>
              Branding
            </Typography>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
            {/* Favicon */}
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                Favicon
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Small icon shown in browser tabs (recommended: 32x32 or 64x64 px)
              </Typography>

              <input
                ref={faviconInputRef}
                type="file"
                accept="image/*,.ico"
                onChange={handleFaviconChange}
                style={{ display: "none" }}
              />

              {site.favicon ? (
                <Box
                  sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    borderRadius: 2,
                    border: 1,
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  })}
                >
                  <Box
                    component="img"
                    src={site.favicon}
                    alt="Favicon"
                    sx={{
                      width: 32,
                      height: 32,
                      objectFit: "contain",
                      borderRadius: 1,
                      bgcolor: "white",
                      p: 0.5,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Favicon uploaded
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => faviconInputRef.current?.click()} disabled={uploadingFavicon}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={handleRemoveFavicon} sx={{ color: "error.main" }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={uploadingFavicon ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                  onClick={() => faviconInputRef.current?.click()}
                  disabled={uploadingFavicon}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    borderStyle: "dashed",
                    py: 2,
                  }}
                >
                  {uploadingFavicon ? "Uploading..." : "Upload Favicon"}
                </Button>
              )}
            </Box>

            {/* Logo */}
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                Logo
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Main logo for your site header (recommended: PNG with transparency)
              </Typography>

              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                style={{ display: "none" }}
              />

              {site.logo ? (
                <Box
                  sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    borderRadius: 2,
                    border: 1,
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  })}
                >
                  <Box
                    component="img"
                    src={site.logo}
                    alt="Logo"
                    sx={{
                      width: 48,
                      height: 32,
                      objectFit: "contain",
                      borderRadius: 1,
                      bgcolor: "white",
                      p: 0.5,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Logo uploaded
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={handleRemoveLogo} sx={{ color: "error.main" }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={uploadingLogo ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    borderStyle: "dashed",
                    py: 2,
                  }}
                >
                  {uploadingLogo ? "Uploading..." : "Upload Logo"}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Analytics */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <AnalyticsIcon sx={{ color: "primary.main" }} />
            <Typography variant="h6" fontWeight={600}>
              Analytics
            </Typography>
          </Box>

          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            Google Analytics Measurement ID
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            GA4 Measurement ID (e.g. G-XXXXXXXXXX). When set, the gtag.js tracking snippet is
            injected into every exported page. Leave empty to disable tracking.
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="G-XXXXXXXXXX"
            value={gaId}
            onChange={(e) => setGaId(e.target.value)}
            onBlur={handleSaveGa}
            error={!gaValid}
            helperText={!gaValid ? "Invalid format — must look like G-XXXXXXXXXX" : " "}
            disabled={savingGa}
            InputProps={{
              endAdornment: savingGa ? <CircularProgress size={16} /> : undefined,
            }}
            sx={{ maxWidth: 360 }}
          />
        </Paper>

        {/* Site URL (Subdomain) */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <LinkIcon sx={{ color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Site URL
              </Typography>
            </Box>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setShowDomainModal(true)}
              sx={{ textTransform: "none" }}
            >
              Edit
            </Button>
          </Box>

          {isCurrentSitePublished && siteUrl ? (
            // This site is published - show its URL
            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderRadius: 2,
                border: 1,
                borderColor: alpha(theme.palette.primary.main, 0.2),
              })}
            >
              <Box>
                <Typography variant="body1" fontWeight={600} sx={{ fontFamily: "monospace", color: "primary.main" }}>
                  {siteUrl}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Your site's primary URL
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => window.open(`https://${siteUrl}`, "_blank")}
                sx={{ color: "primary.main" }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : isSitePublished && publishedSiteName ? (
            // Another site is published - show warning
            <Box
              sx={{
                p: 2,
                bgcolor: "warning.50",
                borderRadius: 2,
                border: 1,
                borderColor: "warning.200",
              }}
            >
              <Typography variant="body2" color="warning.dark" sx={{ mb: 0.5 }}>
                "{publishedSiteName}" is currently published
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Use "Publish แทน" to publish this site instead
              </Typography>
            </Box>
          ) : (
            // No site is published
            <Box
              sx={{
                p: 3,
                bgcolor: "grey.100",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Not published yet
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Publish your site to get a URL
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Custom Domain (Optional) */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <DomainIcon sx={{ color: "text.secondary" }} />
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Custom Domain
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Optional - Use your own domain
                </Typography>
              </Box>
            </Box>
            {!hasCustomDomain && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setShowDomainModal(true)}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Add Domain
              </Button>
            )}
          </Box>

          {hasCustomDomain ? (
            <Box>
              {/* Custom Domain Display */}
              <Box
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  bgcolor: isCustomDomainVerified
                    ? alpha(theme.palette.success.main, 0.04)
                    : alpha(theme.palette.warning.main, 0.04),
                  borderRadius: 2,
                  border: 1,
                  borderColor: isCustomDomainVerified
                    ? alpha(theme.palette.success.main, 0.2)
                    : alpha(theme.palette.warning.main, 0.2),
                  mb: 2,
                })}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="body1" fontWeight={600} sx={{ fontFamily: "monospace" }}>
                    {apiDomain?.domain}
                  </Typography>
                  {isCustomDomainVerified ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
                      <Typography variant="body2" color="success.main" fontWeight={500}>
                        Verified
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <ErrorOutlineIcon sx={{ fontSize: 18, color: "warning.main" }} />
                      <Typography variant="body2" color="warning.main" fontWeight={500}>
                        Pending
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  {!isCustomDomainVerified && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setShowDomainModal(true)}
                      sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                      Continue Setup
                    </Button>
                  )}
                  {isCustomDomainVerified && (
                    <IconButton
                      size="small"
                      onClick={() => window.open(`https://${apiDomain?.domain}`, "_blank")}
                      sx={{ color: "success.main" }}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={() => setShowDomainModal(true)} sx={{ color: "text.secondary" }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={onRemoveDomain} sx={{ color: "error.main" }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Status message */}
              {!isCustomDomainVerified && (
                <Box
                  sx={(theme) => ({
                    bgcolor: alpha(theme.palette.warning.main, 0.08),
                    border: 1,
                    borderColor: alpha(theme.palette.warning.main, 0.3),
                    borderRadius: 2,
                    p: 2,
                  })}
                >
                  <Typography variant="body2" color="warning.dark">
                    Domain setup incomplete. Click "Continue Setup" to complete DNS verification.
                  </Typography>
                </Box>
              )}

              {isCustomDomainVerified && (
                <Box
                  sx={(theme) => ({
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    border: 1,
                    borderColor: alpha(theme.palette.success.main, 0.3),
                    borderRadius: 2,
                    p: 2,
                  })}
                >
                  <Typography variant="body2" color="success.dark">
                    Your custom domain is verified and active. Users can access your site at both URLs.
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                p: 3,
                bgcolor: "grey.50",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <DomainIcon sx={{ fontSize: 40, color: "grey.300", mb: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                No custom domain configured
              </Typography>
              <Typography variant="caption" color="text.secondary">
                You can add your own domain like <strong>yoursite.com</strong>
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Domain Modal */}
      {showDomainModal && (
        <DomainSetupModal
          apiDomain={apiDomain}
          onClose={() => setShowDomainModal(false)}
          onSetDomain={onSetDomain}
          onVerifyDomain={onVerifyDomain}
          onRemove={hasCustomDomain ? onRemoveDomain : undefined}
          subdomain={subdomain}
          subdomainSuffix={subdomainSuffix}
          onSetSubdomain={onSetSubdomain}
          defaultSubdomain={defaultSubdomain}
          isSitePublished={isSitePublished}
          isCurrentSitePublished={isCurrentSitePublished}
          publishedSiteName={publishedSiteName}
          onPublish={onPublish}
          onPublishReplace={onPublishReplace}
        />
      )}
    </Box>
  );
};
